import { debounce, DebouncedFunc } from "lodash";
import {
  action,
  computed,
  flow,
  flowResult,
  makeObservable,
  observable,
} from "mobx";
import { getLendingsAggs } from "../../../api/ApiManager";
import DropdownSelectStore from "../../../components/Selector/stores/SelectorStore";
import {
  IGetOwnerAggsLendingsResponse,
  LendingPageStatus,
  LendingsOwnershipType,
  LendingsSortType,
} from "../../../entities/Lending/LendingTypes";
import BusyStore from "../../../stores/common/BusyStore";
import raceGuard from "../../../utils/raceGuard";

class LendingsFilterStore extends BusyStore {
  readonly statusStore = new DropdownSelectStore(LendingPageStatus.auction);
  readonly collectionsStore = new DropdownSelectStore<string[]>([]);
  readonly sortStore = new DropdownSelectStore(LendingsSortType.createdDesc);
  // readonly ownershipStore = new DropdownSelectStore(LendingsOwnershipType.all);
  readonly ownershipStore: DropdownSelectStore<LendingsOwnershipType>;
  readonly durationsStore = new DropdownSelectStore<number[]>([]);

  lenderAddress: string | null = null;
  borrowerAddress?: string | null = null;

  searchText: string = "";
  debouncedSetSearchText: DebouncedFunc<(value: string) => void> | undefined;

  statuses: {
    status: LendingPageStatus;
    count: number;
  }[] = [];

  collections: {
    collectionAddress: string;
    count: number;
  }[] = [];
  isCollectionsLoading: boolean = false;

  durations: { duration: number; count: number }[] = [];
  isDurationsLoading: boolean = false;

  constructor(private userAddress?: string) {
    super();
    makeObservable<
      LendingsFilterStore,
      "fetchFilterFlow" | "fetchCollectionsFlow" | "fetchDurationsFlow"
    >(this, {
      statuses: observable,
      collections: observable,
      durations: observable,
      isCollectionsLoading: observable,
      searchText: observable,
      lenderAddress: observable,
      borrowerAddress: observable,

      setSearchText: action,
      setCollectionsLoading: action,
      clearFilterAggs: action,
      setLenderAddress: action,
      setBorrowerAddress: action,

      statusFilterList: computed,

      fetchFilterFlow: flow,
      fetchCollectionsFlow: flow,
      fetchDurationsFlow: flow,
    });

    this.ownershipStore = new DropdownSelectStore(
      this.userAddress === "" || !this.userAddress
        ? LendingsOwnershipType.all
        : LendingsOwnershipType.asBorrower
    );
    this.borrowerAddress =
      this.userAddress === "" || !this.userAddress ? null : this.userAddress;

    this.debouncedSetSearchText = debounce(this.setSearchText.bind(this), 500);
  }

  setLenderAddress(address: string | null) {
    this.lenderAddress = address;
  }

  setBorrowerAddress(address: string | null) {
    this.borrowerAddress = address;
  }

  setCollectionsLoading(isLoading: boolean) {
    this.isCollectionsLoading = isLoading;
  }

  setSearchText(text: string) {
    this.debouncedSetSearchText?.cancel();
    this.searchText = text;
  }

  readonly fetchFilter = raceGuard(
    async () => await flowResult(this.fetchFilterFlow())
  );

  private *fetchFilterFlow() {
    try {
      this.onLoadingStart();

      const aggsResponse: IGetOwnerAggsLendingsResponse = yield getLendingsAggs(
        LendingPageStatus.auction,
        this.userAddress,
        []
      );

      this.statuses = aggsResponse.statuses;
      this.collections = aggsResponse.collections;
      this.durations = aggsResponse.durations;
    } catch (error: any) {
      console.log("nik fetchFilterFlow error", error);
    } finally {
      this.onLoadingFinished();
    }
  }

  readonly fetchCollections = (status: LendingPageStatus) =>
    raceGuard(
      async () => await flowResult(this.fetchCollectionsFlow(status))
    )();

  private *fetchCollectionsFlow(status: LendingPageStatus) {
    try {
      this.onLoadingStart();

      const aggsResponse: IGetOwnerAggsLendingsResponse = yield getLendingsAggs(
        status,
        this.userAddress,
        []
      );

      this.collections = aggsResponse.collections;
      this.durations = aggsResponse.durations;
    } catch (error: any) {
      console.log("nik fetchCollectionsFlow error", error);
    } finally {
      this.onLoadingFinished();
    }
  }

  readonly fetchDurations = (
    status: LendingPageStatus,
    collections: string[]
  ) =>
    raceGuard(
      async () => await flowResult(this.fetchDurationsFlow(status, collections))
    )();

  private *fetchDurationsFlow(
    status: LendingPageStatus,
    collections: string[]
  ) {
    try {
      this.onLoadingStart();

      const aggsResponse: IGetOwnerAggsLendingsResponse = yield getLendingsAggs(
        status,
        this.userAddress,
        collections
      );

      this.durations = aggsResponse.durations;
    } catch (error: any) {
      console.log("nik fetchCollectionsFlow error", error);
    } finally {
      this.onLoadingFinished();
    }
  }

  clearFilterAggs() {
    this.sortStore.setSelectedOption(LendingsSortType.createdDesc);
    this.statusStore.setSelectedOption(LendingPageStatus.auction);
    this.searchText = "";
    this.collectionsStore.setSelectedOption([]);
    this.lenderAddress = null;
    this.borrowerAddress = null;
    this.ownershipStore.setSelectedOption(LendingsOwnershipType.all);
  }

  get statusFilterList() {
    return [
      {
        label: "Waiting offers",
        value: LendingPageStatus.auction,
        count:
          this.statuses.find(
            (status) => status.status === LendingPageStatus.auction
          )?.count || 0,
      },
      {
        label: "Active",
        value: LendingPageStatus.active,
        count:
          this.statuses.find(
            (status) => status.status === LendingPageStatus.active
          )?.count || 0,
      },
      {
        label: "Repaid",
        value: LendingPageStatus.repaid,
        count:
          this.statuses.find(
            (status) => status.status === LendingPageStatus.repaid
          )?.count || 0,
      },
      {
        label: "Overdue",
        value: LendingPageStatus.overdue,
        count:
          this.statuses.find(
            (status) => status.status === LendingPageStatus.overdue
          )?.count || 0,
      },
    ];
  }
}

export default LendingsFilterStore;
